import useSWR from "swr";
import useSWRMutation from "swr/mutation";
import { useCallback, useEffect } from "react";
import { useNotify } from "react-admin";

// start of commands
export const INFO_DENSITY = 1;
export const INFO_PRINTSPEED = 2;
export const INFO_LABELTYPE = 3;
export const INFO_LANGUAGETYPE = 6;
export const INFO_AUTOSHUTDOWNTIME = 7;
export const INFO_DEVICETYPE = 8;
export const INFO_SOFTVERSION = 9;
export const INFO_BATTERY = 10;
export const INFO_DEVICESERIAL = 11;
export const INFO_HARDVERSION = 12;

export const CMD_GET_INFO = 64; // 0x40
export const CMD_GET_RFID = 26; // 0x1A
export const CMD_HEARTBEAT = 220; // 0xDC
export const CMD_SET_LABEL_TYPE = 35; // 0x23
export const CMD_SET_LABEL_DENSITY = 33; // 0x21
export const CMD_START_PRINT = 1; // 0x01
export const CMD_END_PRINT = 243; // 0xF3
export const CMD_START_PAGE_PRINT = 3; // 0x03
export const CMD_END_PAGE_PRINT = 227; // 0xE3
export const CMD_ALLOW_PRINT_CLEAR = 32; // 0x20
export const CMD_SET_DIMENSION = 19; // 0x13
export const CMD_SET_QUANTITY = 21; // 0x15
export const CMD_GET_PRINT_STATUS = 163; // 0xA3
export const CMD_IMAGE_SET = 131; // 0x83
export const CMD_IMAGE_CLEAR = 132;
export const CMD_IMAGE_DATA = 133; // 0x85
export const CMD_IMAGE_RECEIVED = 0xd3; // ushort offset, byte last line
//end of commands

export const SERIAL_PORT_CONNECTED = "SERIAL_PORT_CONNECTED";
export const SERIAL_PORT_SIGNAL = "SERIAL_PORT_SIGNAL";
export const SERIAL_PORT_SEND_QUEUE = "SERIAL_PORT_SEND_QUEUE";
export const PRINT_JOB_QUEUE = "PRINT_JOBS_QUEUE";
export const SERIAL_PORT_SEND_RAW_DATA = "SERIAL_PORT_SEND_RAW_DATA";
export const SERIAL_PORT_RECEIVE_RAW_DATA = "SERIAL_PORT_RECEIVE_RAW_DATA";
export const SERIAL_PORT_WAIT_RECEIVE = "SERIAL_PORT_WAIT_RECEIVE";
export const NIIMBOT_IS_PRINTING = "NIIMBOT_IS_PRINTING";
export const GET_PRINT_PROGRESS = "GET_PRINT_PROGRESS";

const LOOP_BREAK = 9999;
const PORT_FILTER_B21S = {
  // usbProductId: 2,
  usbVendorId: 13587,
};
export const useSerialPort = () => {
  return useSWR(SERIAL_PORT_CONNECTED, () => null, {
    revalidateOnFocus: false,
  });
};

export const useSerialGetSignal = () => {
  const { data: serialPort, mutate } = useSerialPort();
  return useSWR(
    serialPort ? SERIAL_PORT_SIGNAL : null,
    () => serialPort.getSignals(),
    {
      onError: (err) => {
        console.log(err);
        console.log("PORT CLOSED");
        mutate(undefined, { revalidate: false });
      },
      shouldRetryOnError: false,
      refreshInterval: 5000,
    }
  );
};

export const useSerialSendQueue = () => {
  return useSWR(SERIAL_PORT_SEND_QUEUE, () => [], {
    revalidateOnFocus: false,
  });
};

export const usePrintJobQueue = () => {
  return useSWR(PRINT_JOB_QUEUE, () => [], {
    revalidateOnFocus: false,
  });
};

//this is the orchestrator
export const useNiimbotPrinter = () => {
  const notify = useNotify();
  const { trigger: send } = useSendRawData();
  const { data: serialPort, mutate: mutateSerialPort } = useSerialPort();
  useSerialGetSignal();
  const { data: sendQueue, mutate: mutateSendQueue } = useSerialSendQueue();
  const { data: printJobQueue = [], mutate: mutatePrintJobQueue } =
    usePrintJobQueue();
  const { data: waitReceive, mutate: mutateWaitReceive } =
    useSerialWaitReceive();
  const { data: isPrinting, mutate: mutateIsPrinting } = useIsPrinting();
  useSerialReceive();

  useSWR(
    isPrinting ? GET_PRINT_PROGRESS : null,
    () => {
      // console.log("print", "GET_PRINT_PROGRESS");
      const getProgressPacket = niimbotToPacket(CMD_GET_PRINT_STATUS, [1]);
      send([
        {
          data: getProgressPacket,
          waitReceive: true,
          isPrinting: true,
        },
      ]); //send get print  status
      return Promise.resolve(true);
    },
    { refreshInterval: 500, dedupingInterval: 200 }
  );

  const isSendQueueEmpty = !(
    sendQueue &&
    Array.isArray(sendQueue) &&
    sendQueue.length > 0
  );

  const handleConnect = useCallback(async () => {
    try {
      const ports = await navigator.serial.getPorts({
        filters: [PORT_FILTER_B21S],
      });
      if (ports.length === 1) {
        const prevPort = ports[0];
        await prevPort.open({ baudRate: 115200 });
        mutateSerialPort(prevPort, { revalidate: false });
      } else {
        const port = await navigator.serial.requestPort({
          filters: [PORT_FILTER_B21S],
        });
        await port.open({ baudRate: 115200 });
        mutateSerialPort(port, { revalidate: false });
      }
    } catch (error) {
      console.error("Serial port connect failed", error);
      notify(error.message, { type: "error" });
    }
  }, [mutateSerialPort, notify]);

  useEffect(() => {
    if (
      !waitReceive &&
      !isPrinting &&
      isSendQueueEmpty &&
      printJobQueue.length > 0
    ) {
      //take next printJob from printJobQueue and spread packets into sendQueue
      mutateSendQueue([...printJobQueue[0]], { revalidate: false });
      mutatePrintJobQueue((prev) => prev.slice(1), { revalidate: false });
    }

    if (serialPort) {
      if (!isPrinting && !waitReceive && !isSendQueueEmpty) {
        //default will wait for receive when command sent, aka transceiver mode
        //no need wait when sending image data
        //when end page print also should set isPrinting = true to wait for print completes
        // send(sendQueue[0]);
        //remove first sent element on queue
      }
    } else if (!isSendQueueEmpty && !serialPort) {
      handleConnect();
    }
  }, [
    handleConnect,
    isPrinting,
    isSendQueueEmpty,
    mutatePrintJobQueue,
    mutateSendQueue,
    mutateSerialPort,
    printJobQueue,
    send,
    sendQueue,
    serialPort,
    waitReceive,
  ]);

  useEffect(() => {
    //close serial port upon unmount
    return async () => {
      if (serialPort) {
        try {
          console.log("Close serial");
          await serialPort.close();
          mutateSerialPort(undefined, { revalidate: false });
        } catch (error) {
          console.error(error);
        }
      }
    };
  }, [mutateSerialPort, serialPort]);
};

export const useSendRawData = (option) => {
  const { data: serialPort, mutate: mutateSerialPort } = useSerialPort();
  const { data: waitReceive, mutate: mutateWaitReceive } =
    useSerialWaitReceive();
  const { data: isPrinting, mutate: mutateIsPrinting } = useIsPrinting();
  const { data: sendQueue, mutate: mutateSendQueue } = useSerialSendQueue();

  const mutation = useSWRMutation(
    // SERIAL_PORT_SEND_RAW_DATA,
    serialPort,
    async (port, { arg }) => {
      //TODO add send no wait receive
      const chunkSize = 200;
      const writer = port.writable.getWriter();
      for (const singleData of arg) {
        const { data } = singleData;
        for (let i = 0; i < data.length; i += chunkSize) {
          const chunk = data.slice(i, i + chunkSize);
          const newData = new Uint8Array(chunk);
          // console.log("SEND DATA", newData);
          await writer.write(newData);
        }
      }
      // Allow the serial port to be closed later.
      await writer.releaseLock(); //release lock after success

      return arg;
    },
    {
      use: [],
      onSuccess: (datas) => {
        mutateSendQueue((prev) => prev.slice(datas.length), {
          revalidate: false,
        });
        const mergedWaitReceive = datas.some(
          (singleData) => singleData.waitReceive
        );
        const mergedIsPrinting = datas.some(
          (singleData) => singleData.isPrinting
        );

        mutateWaitReceive(mergedWaitReceive, { revalidate: false });
        mutateIsPrinting(mergedIsPrinting, { revalidate: false });

        // mutateWaitReceive(waitReceive, { revalidate: false });
        // console.log("mutateWaitReceive", waitReceive);
        // mutateIsPrinting(isPrinting, { revalidate: false });
      },
      onError: (err) => {
        console.error("Send serial error", err);
        mutateSerialPort(undefined, { revalidate: false });
      },
      shouldRetryOnError: false,
      ...option,
    }
  );
  const { trigger: send } = mutation;
  const isSendQueueEmpty = !(
    sendQueue &&
    Array.isArray(sendQueue) &&
    sendQueue.length > 0
  );

  useEffect(() => {
    if (serialPort) {
      if (!isPrinting && !waitReceive && !isSendQueueEmpty) {
        //default will wait for receive when command sent, aka transceiver mode
        //no need wait when sending image data
        //when end page print also should set isPrinting = true to wait for print completes
        const sliceIndex = sendQueue.findIndex(
          (item) => item.waitReceive || item.isPrinting
        );

        send(
          sendQueue.slice(
            0,
            sliceIndex === -1 ? sendQueue.length : sliceIndex + 1
          )
        );

        //remove first sent element on queue
      }
    }
  }, [isPrinting, isSendQueueEmpty, send, sendQueue, serialPort, waitReceive]);
  return mutation;
};

export const useSerialWaitReceive = () =>
  useSWR(SERIAL_PORT_WAIT_RECEIVE, () => false, {
    revalidateOnFocus: false,
  });

export const useIsPrinting = () =>
  useSWR(NIIMBOT_IS_PRINTING, () => false, {
    revalidateOnFocus: false,
  });

export const useSerialReceive = () => {
  const { data: serialPort } = useSerialPort();
  const { mutate: mutateSendQueue } = useSerialSendQueue();
  const { mutate: mutateIsPrinting } = useIsPrinting();
  const { data: waitReceive, mutate: mutateWaitReceive } =
    useSerialWaitReceive();

  return useSWR(
    waitReceive ? SERIAL_PORT_RECEIVE_RAW_DATA : null,
    async () => {
      let receivedBytes = new Uint8Array();
      if (serialPort.readable) {
        const reader = serialPort.readable.getReader();
        for (let i = 0; i < LOOP_BREAK; i++) {
          //use for loop to prevent forever looping
          const { value, done } = await reader.read();
          receivedBytes = concatenateUint8Arrays(receivedBytes, value);
          // console.log("receivedBytes", receivedBytes);
          if (
            done ||
            (receivedBytes.length > 1 &&
              receivedBytes[receivedBytes.length - 1] === 170 &&
              receivedBytes[receivedBytes.length - 2] === 170) //170 is packet ending suffix 0xaa
          ) {
            // Allow the serial port to be closed later.
            reader.releaseLock();
            return Promise.resolve(receivedBytes);

            // return Promise.resolve(niimbotFromPacket(receivedBytes));
          } else if (i >= LOOP_BREAK) {
            throw new Error("Printer no response, please reset.");
          }
        }
      }
      console.log("print", "NO receive data");
      return Promise.resolve([]);
      // return Promise.resolve(niimbotFromPacket(receivedBytes));
    },
    {
      // revalidateOnFocus: false, //Should revalidate receiving end
      refreshInterval: 500,
      dedupingInterval: 200,
      onSuccess: (packets) => {
        if (packets && packets.length > 0) {
          const packetsStr = packets.toString(); //convert to string to split multi packets

          const receivedDatas = packetsStr
            .replaceAll("170,170,", "170,170|")
            .split("|")
            .map((s) => s.split(",").map(Number))
            .map((arr) => new Uint8Array(arr))
            .map((receivedBytes) => niimbotFromPacket(receivedBytes));

          receivedDatas.forEach((data) => {
            const [recvType, recvData] = data;
            //responseType check
            if (recvType == 219) {
              throw "error";
            } else if (recvType == 0) {
              throw "not implemented";
            } else if (recvType === CMD_GET_PRINT_STATUS + 16) {
              //https://github.com/AndBondStyle/niimprint/issues/3
              const page = recvData[0] * 256 + recvData[1];
              const progress1 = recvData[2];
              const progress2 = recvData[3];
              const printError = recvData[6];
              if (printError) {
                console.error("PRINT ERROR");
                mutateIsPrinting(false, { revalidate: false });
              } else if (progress1 === 100 && progress2 === 100) {
                mutateIsPrinting(false, { revalidate: false });

                //End print
                setTimeout(() => {
                  mutateSendQueue(
                    (prev) => [
                      ...prev,
                      {
                        data: niimbotToPacket(CMD_END_PRINT, [1]),
                        isPrinting: false,
                      },
                    ],
                    {
                      revalidate: false,
                    }
                  );
                }, 1000);
              } else {
                //print print progress
                // console.log("PRINT PROGRESS", {
                //   page,
                //   progress1,
                //   progress2,
                //   printError,
                // });
              }
            } else {
              // const receivedData = arrayToHexString(data);
              // console.log("originalReceivedData", data);
              // console.log("receivedDataHex", receivedData);
            }
            mutateWaitReceive(false, { revalidate: false });
          });
        }
      },
      onError: (err) => {
        console.error("RECEIVE ERROR, Resetting.", err);
        mutateSendQueue([], { revalidate: false });
        mutateWaitReceive(false, { revalidate: false });
        alert("RECEIVE ERROR, Resetting.");
      },
      shouldRetryOnError: false,
    }
  );
};

// const useTransceivePacket = () => {

// }

// below are helper functions
export const concatenateUint8Arrays = (a, b) => {
  const result = new Uint8Array(a.length + b.length);
  result.set(a);
  result.set(b, a.length);
  return result;
};

export const niimbotFromPacket = (packet) => {
  packet = byteArrayToArray(packet);

  if (packet.shift() != 0x55 || packet.shift() != 0x55) return [];
  if (packet.pop() != 0xaa || packet.pop() != 0xaa) return [];

  let checksum = packet.pop();
  packet.forEach((byte) => (checksum ^= byte));
  if (checksum != 0) return [];

  let type = packet.shift();
  let len = packet.shift();
  if (packet.length != len) return [];

  return [type, packet];
};

export const byteArrayToArray = (buffer) => {
  return Array.from(buffer);
};

export const arrayToHexString = (array, join = "") => {
  return array.map((el) => ("00" + el.toString(16)).slice(-2)).join(join);
};

export const niimbotToPacket = (type, data) => {
  let packet = [type, data.length].concat(data);

  let checksum = 0;
  packet.forEach((byte) => (checksum ^= byte));

  // add header: 2x 0x55
  packet.unshift(0x55);
  packet.unshift(0x55);

  // add footer: checksum + 2x 0xAA
  packet.push(checksum);
  packet.push(0xaa);
  packet.push(0xaa);

  return packet;
};

export const niimbotMmToPx = (x) => {
  return Math.ceil((x / 25.4) * 203);
};

export const niimbotPxToMM = (x) => {
  return Math.floor((x / 203) * 25.4);
};

export const ushortToByteArray = (value) => {
  console.assert(0 <= value && value <= 65535);
  return [Math.floor(value / 256), value % 256];
};
export const niimbotSetLabelDimensions = (w, h) => {
  console.assert(1 <= w && w <= niimbotMmToPx(50));
  console.assert(1 <= h && h <= niimbotMmToPx(30));
  return niimbotToPacket(CMD_SET_DIMENSION, [
    ...ushortToByteArray(h),
    ...ushortToByteArray(w),
  ]);
};

export const makeNiimbotSendImagePackets = (w, h, imageData, sliceSize = 1) => {
  let packets = [];
  for (let ySlice = 0; ySlice < h; ySlice++) {
    let dataNext = imageData.slice(ySlice * w, (ySlice + 1) * w);

    const linePacket = niimbotPacketImageData(ySlice, w, dataNext, 1);
    // console.log("Image linePacket", linePacket);
    packets.push(linePacket);

    // promise = await niimbotTransceiveRawData(packet, CMD_IMAGE_RECEIVED);
  }
  return packets;
};

export const niimbotPacketImageData = (y, w, data, n = 1) => {
  let buffer = [];
  buffer.push(...ushortToByteArray(y));
  buffer.push(...[0, 0, 0, 1]);

  for (let x = 0; x < w; x += 8) {
    let bits = 0;
    for (let b = 0; b < 8; b++) if (data[x + b]) bits |= 1 << (7 - b);
    buffer.push(bits);
  }
  return niimbotToPacket(CMD_IMAGE_DATA, buffer);
};

export const fillTextMultiLine = (ctx, text, x, y) => {
  var lineHeight = ctx.measureText("M").width * 1.2;
  var lines = text.split("\n");
  for (var i = 0; i < lines.length; ++i) {
    ctx.fillText(lines[i], x, y);
    y += lineHeight;
  }
};
